import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { manageLists as initialLists, ManageList, ListCommunityEntry, PipelineStatus, ListType } from "@/data/manage-lists";
import { communities } from "@/data/communities";
import { toast } from "@/hooks/use-toast";

const parentThemeMap: Record<string, string> = {
  "Running": "Sport & Running",
  "Trail Running": "Sport & Running",
  "Yoga": "Wellness & Yoga",
  "Wellness": "Wellness & Yoga",
  "Mindfulness": "Wellness & Yoga",
  "Meditation": "Wellness & Yoga",
  "Cooking": "Food & Culinary",
  "Culinary": "Food & Culinary",
  "Gastronomy": "Food & Culinary",
  "Chess": "Strategy & Games",
  "Board Games": "Strategy & Games",
  "Street Art": "Art & Culture",
  "Art": "Art & Culture",
  "Culture": "Art & Culture",
  "Sustainability": "Sustainability",
  "Tech": "Tech & Innovation",
  "Innovation": "Tech & Innovation",
  "AI": "Tech & Innovation",
  "Literature": "Culture & Learning",
  "Philosophy": "Culture & Learning",
  "Creative Writing": "Culture & Learning",
  "Cycling": "Sport & Cycling",
  "Bike Touring": "Sport & Cycling",
  "Urban Cycling": "Sport & Cycling",
  "Photography": "Visual & Photo",
  "Visual Arts": "Visual & Photo",
  "Digital Art": "Visual & Photo",
  "Coffee": "Coffee & Craft",
  "Specialty Coffee": "Coffee & Craft",
  "Entrepreneurship": "Business & Startup",
  "Business": "Business & Startup",
  "Networking": "Business & Startup",
  "Fitness": "Sport & Fitness",
};

function getParentTheme(firstTheme: string): string {
  return parentThemeMap[firstTheme] || firstTheme;
}

interface ManageListsContextType {
  lists: ManageList[];
  createList: (name: string, listType: ListType, seedCommunityIds?: string[], description?: string) => ManageList;
  deleteList: (id: string) => void;
  addCommunityToList: (listId: string, communityId: string) => void;
  removeCommunityFromList: (listId: string, entryId: string) => void;
  isCommunityInAnyList: (communityId: string) => boolean;
  isCommunityInList: (listId: string, communityId: string) => boolean;
  getListsForCommunity: (communityId: string) => ManageList[];
  updateEntryStatus: (entryId: string, status: PipelineStatus) => void;
  updateEntryAssignee: (entryId: string, assignee: { name: string; initials: string }) => void;
  updateListDescription: (listId: string, description: string) => void;
  setLists: React.Dispatch<React.SetStateAction<ManageList[]>>;
}

const ManageListsContext = createContext<ManageListsContextType>({
  lists: [],
  createList: () => ({} as ManageList),
  deleteList: () => {},
  addCommunityToList: () => {},
  removeCommunityFromList: () => {},
  isCommunityInAnyList: () => false,
  isCommunityInList: () => false,
  getListsForCommunity: () => [],
  updateEntryStatus: () => {},
  updateEntryAssignee: () => {},
  updateListDescription: () => {},
  setLists: () => {},
});

// Default "Saved" list that always exists
const DEFAULT_SAVED_LIST: ManageList = {
  id: "list-saved-default",
  name: "Saved Communities",
  listType: "interest",
  communities: [],
  createdBy: { name: "You", initials: "YO" },
  createdDate: new Date().toISOString().slice(0, 10),
  lastUpdated: new Date().toISOString().slice(0, 10),
  interestGroups: [{ id: "ig-default", label: "Saved" }],
};

function buildEntryFromCommunityId(communityId: string): ListCommunityEntry | null {
  const c = communities.find(cm => cm.id === communityId);
  if (!c) return null;
  return {
    id: `entry-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    communityId: c.id,
    communityName: c.name,
    theme: getParentTheme(c.theme[0] || ""),
    city: c.location,
    location: `${c.location}, France`,
    status: "shortlisted",
    fitScore: c.fitScore,
    activeMembers: c.activeMembers,
    reach: c.totalReach >= 1000 ? `${Math.round(c.totalReach / 1000)}K` : `${c.totalReach}`,
    density: c.networkDensity,
    assignee: { name: "You", initials: "YO" },
    docs: 0,
    notes: "",
    documents: [],
  };
}

export const ManageListsProvider = ({ children }: { children: ReactNode }) => {
  const [lists, setLists] = useState<ManageList[]>([DEFAULT_SAVED_LIST, ...initialLists]);

  const createList = useCallback((name: string, listType: ListType, seedCommunityIds?: string[], description?: string) => {
    const seedEntries = (seedCommunityIds || [])
      .map(buildEntryFromCommunityId)
      .filter((e): e is ListCommunityEntry => e !== null);
    const newList: ManageList = {
      id: `list-${Date.now()}`,
      name,
      listType,
      communities: seedEntries,
      createdBy: { name: "You", initials: "YO" },
      createdDate: new Date().toISOString().slice(0, 10),
      lastUpdated: new Date().toISOString().slice(0, 10),
      ...(listType === "campaign" ? { campaignStatus: "planning" as const } : {}),
      ...(listType === "interest" ? { 
        interestGroups: [{ id: `ig-${Date.now()}`, label: "General" }],
        ...(description ? { topicTheme: description } : {}),
      } : {}),
    };
    setLists(prev => [...prev, newList]);
    return newList;
  }, []);

  const updateListDescription = useCallback((listId: string, description: string) => {
    setLists(prev => prev.map(list => 
      list.id === listId ? { ...list, topicTheme: description, lastUpdated: new Date().toISOString().slice(0, 10) } : list
    ));
  }, []);

  const deleteList = useCallback((id: string) => {
    setLists(prev => prev.filter(l => l.id !== id));
  }, []);

  const addCommunityToList = useCallback((listId: string, communityId: string) => {
    const entry = buildEntryFromCommunityId(communityId);
    if (!entry) return;
    setLists(prev => prev.map(list => {
      if (list.id !== listId) return list;
      if (list.communities.some(c => c.communityId === communityId)) return list;
      return { ...list, communities: [...list.communities, entry], lastUpdated: new Date().toISOString().slice(0, 10) };
    }));
  }, []);

  const removeCommunityFromList = useCallback((listId: string, entryId: string) => {
    setLists(prev => prev.map(list => {
      if (list.id !== listId) return list;
      return { ...list, communities: list.communities.filter(c => c.id !== entryId), lastUpdated: new Date().toISOString().slice(0, 10) };
    }));
  }, []);

  const isCommunityInAnyList = useCallback((communityId: string) => {
    return lists.some(l => l.communities.some(c => c.communityId === communityId));
  }, [lists]);

  const isCommunityInList = useCallback((listId: string, communityId: string) => {
    const list = lists.find(l => l.id === listId);
    return list?.communities.some(c => c.communityId === communityId) ?? false;
  }, [lists]);

  const getListsForCommunity = useCallback((communityId: string) => {
    return lists.filter(l => l.communities.some(c => c.communityId === communityId));
  }, [lists]);

  const updateEntryStatus = useCallback((entryId: string, status: PipelineStatus) => {
    setLists(prev => prev.map(list => ({
      ...list,
      communities: list.communities.map(c => c.id === entryId ? { ...c, status } : c),
    })));
  }, []);

  const updateEntryAssignee = useCallback((entryId: string, assignee: { name: string; initials: string }) => {
    setLists(prev => prev.map(list => ({
      ...list,
      communities: list.communities.map(c => c.id === entryId ? { ...c, assignee } : c),
    })));
  }, []);

  return (
    <ManageListsContext.Provider value={{
      lists, setLists, createList, deleteList,
      addCommunityToList, removeCommunityFromList,
      isCommunityInAnyList, isCommunityInList,
      getListsForCommunity, updateEntryStatus, updateEntryAssignee,
      updateListDescription,
    }}>
      {children}
    </ManageListsContext.Provider>
  );
};

export const useManageLists = () => useContext(ManageListsContext);
